import type { QCStandardByIdResult } from "@/modules/qc/domain/types";
import { formatDateLocal, formatDateTimeLocal } from "@/shared/lib/date-time";

interface QCPdfPreviewContentProps {
  qc: QCStandardByIdResult;
}

export function QCPdfPreviewContent({ qc }: QCPdfPreviewContentProps) {
  return (
    <div
      id="qc-pdf-content"
      className="mx-auto max-w-4xl bg-white p-8 font-serif text-gray-900 leading-relaxed print:max-w-none print:p-0 print:font-serif print:text-gray-900 print:leading-relaxed"
      style={{
        fontFamily: '"Times New Roman", Times, serif',
        lineHeight: "1.5",
        color: "#1a1a1a",
      }}
    >
      {/* PDF Header - Letterhead Style */}
      <div className="mb-8 border-gray-800 border-b-4 pb-6 print:mb-6">
        <div className="mb-6 text-center">
          <h1 className="font-bold text-4xl text-gray-800 uppercase tracking-wider">
            FREEDOM Sole Co.Ltd
          </h1>
          <p className="mt-2 text-gray-600 text-lg">ມາດຕະຖານການກວດສອບ QC</p>
          <div className="mx-auto mt-4 h-1 w-24 bg-gray-800" />
        </div>

        <div className="flex items-start justify-between">
          <div className="text-sm">
            <p className="font-semibold">
              ເອກະສານເລກທີ:{" "}
              <span className="font-normal">{`QC-${qc?.id.slice(-6).toUpperCase()}`}</span>
            </p>
            <p className="mt-1 font-semibold">
              ສະບັບທີ: <span className="font-normal">1.0</span>
            </p>
          </div>
          <div className="text-right text-sm">
            <p className="font-semibold">ວັນທີອັບເດດລ່າສຸດ:</p>
            <p className="font-normal">{formatDateLocal(qc?.updatedAt ?? "")}</p>
            <p className="mt-2 font-semibold">ວັນທີພິມ:</p>
            <p className="font-normal">{formatDateLocal(new Date())}</p>
          </div>
        </div>
      </div>

      {/* QC Title and Status */}
      <div className="mb-12 text-center">
        <h2 className="mb-6 border-gray-300 border-b-2 pb-4 font-bold text-3xl text-gray-800 uppercase tracking-wide">
          {qc?.name}
        </h2>
      </div>

      {/* Section 1: ข้อมูลพื้นฐาน */}
      <div className="mb-10">
        <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
          1. ຂໍ້ມູນພື້ນຖານ
        </h3>

        <table className="mb-6 w-full border-collapse border-2 border-gray-400">
          <tbody>
            <tr className="border-gray-300 border-b">
              <td className="w-1/3 border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ປະເພດງານ
              </td>
              <td className="p-4 text-sm leading-relaxed">
                {qc?.workTypeName || "ບໍ່ໄດ້ກໍານົດ"}
              </td>
            </tr>
            {qc?.workStage && (
              <tr className="border-gray-300 border-b">
                <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                  ຂັ້ນຕອນການດໍາເນີນງານ
                </td>
                <td className="p-4 text-sm">{qc?.workStage}</td>
              </tr>
            )}
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ພະແນກທີ່ກ່ຽວຂ້ອງ
              </td>
              <td className="p-4 text-sm">{qc?.departmentName || "ບໍ່ໄດ້ກໍານົດ"}</td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ຜູ້ກວດສອບ
              </td>
              <td className="p-4 text-sm">
                {qc?.inspectorPositionTitle || "ບໍ່ໄດ້ກໍານົດ"}
              </td>
            </tr>
            <tr>
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ວັນທີອັບເດດລ່າສຸດ
              </td>
              <td className="p-4 text-sm">
                {formatDateTimeLocal(qc?.updatedAt ?? "")}
              </td>
            </tr>
          </tbody>
        </table>

        {qc?.description && (
          <div className="mb-6">
            <h4 className="mb-3 font-semibold text-gray-700 text-sm uppercase tracking-wide">
              ລາຍລະອຽດ
            </h4>
            <p className="text-base text-gray-800 leading-relaxed">
              {qc?.description}
            </p>
          </div>
        )}
      </div>

      {/* Section 2: รายการตรวจ */}
      {qc?.items && qc?.items.length > 0 && (
        <div className="mb-10">
          <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
            2. ລາຍການກວດສອບ
          </h3>

          <div className="space-y-4">
            {qc.items.map((item, index) => (
              <div
                key={item.id}
                className="rounded-lg border-2 border-gray-300 p-4"
              >
                <div className="mb-3 flex items-start justify-between">
                  <h4 className="font-bold text-base text-gray-800">
                    {index + 1}. {item.title}
                    {item.isRequired && (
                      <span className="ml-2 font-normal text-gray-500 text-sm">
                        (ຈໍາເປັນ)
                      </span>
                    )}
                  </h4>
                </div>
                {item.description && (
                  <p className="mb-3 text-gray-700 text-sm leading-relaxed">
                    {item.description}
                  </p>
                )}
                {item.passCriteria && (
                  <div className="mb-2 rounded bg-green-50 p-2">
                    <p className="font-semibold text-green-800 text-sm">
                      ຜ່ານ: {item.passCriteria}
                    </p>
                  </div>
                )}
                {item.failCriteria && (
                  <div className="rounded bg-red-50 p-2">
                    <p className="font-semibold text-red-800 text-sm">
                      ບໍ່ຜ່ານ: {item.failCriteria}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section 3: เกณฑ์ผ่าน / ไม่ผ่าน */}
      {(qc?.passCriteria || qc?.failCriteria) && (
        <div className="mb-10">
          <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
            3. ເກນຜ່ານ / ບໍ່ຜ່ານ
          </h3>

          {qc?.passCriteria && (
            <div className="mb-4">
              <h4 className="mb-2 font-semibold text-gray-700 text-sm uppercase tracking-wide">
                ເກນຜ່ານ:
              </h4>
              <div className="rounded-lg border-2 border-green-300 bg-green-50 p-4">
                <p className="text-base text-gray-800 leading-relaxed">
                  {qc?.passCriteria}
                </p>
              </div>
            </div>
          )}

          {qc?.failCriteria && (
            <div>
              <h4 className="mb-2 font-semibold text-gray-700 text-sm uppercase tracking-wide">
                ເກນບໍ່ຜ່ານ:
              </h4>
              <div className="rounded-lg border-2 border-red-300 bg-red-50 p-4">
                <p className="text-base text-gray-800 leading-relaxed">
                  {qc?.failCriteria}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Section 4: แนวทางเมื่อไม่ผ่าน */}
      {qc?.actionOnFail && (
        <div className="mb-10">
          <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
            4. ແນວທາງເມື່ອບໍ່ຜ່ານ
          </h3>

          <div className="mb-4">
            <p className="text-base text-gray-800 leading-relaxed">
              {qc?.actionOnFail}
            </p>
          </div>

          {qc?.notifyPositionTitle && (
            <div className="mb-4">
              <h4 className="mb-2 font-semibold text-gray-700 text-sm uppercase tracking-wide">
                ສົ່ງແຈ້ງໃຫ້:
              </h4>
              <p className="text-base text-gray-800">
                {qc?.notifyPositionTitle}
              </p>
            </div>
          )}

          {(qc?.requirePhoto || qc?.requireNotesOnFail) && (
            <div className="mt-4">
              <h4 className="mb-2 font-semibold text-gray-700 text-sm uppercase tracking-wide">
                ຂໍ້ກໍານົດເພີ່ມເຕີມ:
              </h4>
              <ul className="list-disc pl-6 text-base text-gray-800">
                {qc?.requirePhoto && <li>ຕ້ອງມີຮູບພາບ</li>}
                {qc?.requireNotesOnFail && <li>ຕ້ອງມີໝາຍເຫດ</li>}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="mt-16 border-gray-300 border-t-2 pt-6 text-center text-gray-600 text-xs">
        <p>ເອກະສານນີ້ຖືກສ້າງຂຶ້ນເພື່ອໃຊ້ໃນການກວດສອບຄຸນນະພາບຜະລິດຕະພັນ</p>
        <p className="mt-2">
          ເອກະສານເລກທີ: QC-{qc?.id?.slice(-6).toUpperCase()} | ສະບັບ: 1.0 | ວັນທີ:
          {formatDateLocal(new Date())}
        </p>
      </div>
    </div>
  );
}
